const React = require('react');
const { createElement: h, useState, useRef, useEffect } = React;
const { request, requestStream, minimizeArrays } = require('../lib/apiClient');
const { resolveParamValues } = require('../lib/resolveAtRefs');
const AskActivityLog = require('./AskActivityLog');
const ConfirmDialog = require('./ConfirmDialog');
require('./CustomObjectEditor.css');

var MONACO_CDN = 'https://cdn.jsdelivr.net/npm/monaco-editor@0.44.0/min/vs';
var monacoLoadPromise = null;
var NODE_BORDER_COLOR_OPTIONS = [
  { value: '', label: 'Default' },
  { value: '#4165bc', label: 'Royal blue' },
  { value: '#5ea8cf', label: 'Sky blue' },
  { value: '#86a22c', label: 'Olive' },
  { value: '#57a187', label: 'Sea green' },
  { value: '#c24e22', label: 'Burnt orange' },
  { value: '#46835e', label: 'Forest green' },
  { value: '#bfd05a', label: 'Lime' },
  { value: '#6f8bc8', label: 'Periwinkle' },
  { value: '#bcc9e5', label: 'Mist' },
  { value: '#e79863', label: 'Peach' },
  { value: '#9a9587', label: 'Stone' },
  { value: '#7c6839', label: 'Bronze' },
  { value: '#d28d99', label: 'Blush' },
  { value: '#b6656a', label: 'Dusty rose' },
  { value: '#eba085', label: 'Soft coral' }
];

// Render a short "captured 3w ago" hint so users know when the cached
// upstream output sample was recorded. Returns '' when no timestamp.
function formatLastOutputStaleness(iso) {
  if (!iso) return '';
  var t = Date.parse(iso);
  if (!Number.isFinite(t)) return '';
  var diffMs = Date.now() - t;
  if (diffMs < 0) diffMs = 0;
  var minutes = Math.floor(diffMs / 60000);
  var hours = Math.floor(minutes / 60);
  var days = Math.floor(hours / 24);
  var weeks = Math.floor(days / 7);
  var label;
  if (minutes < 1) label = 'just now';
  else if (minutes < 60) label = minutes + 'm ago';
  else if (hours < 24) label = hours + 'h ago';
  else if (days < 7) label = days + 'd ago';
  else label = weeks + 'w ago';
  return '// upstream output sample captured ' + label;
}

function formatHyperparamsApplied(applied) {
  if (!applied || typeof applied !== 'object') return '';
  var parts = [];
  if (Array.isArray(applied.added) && applied.added.length) {
    parts.push('Added: ' + applied.added.map(function(a) { return a.key; }).join(', '));
  }
  if (Array.isArray(applied.removed) && applied.removed.length) {
    parts.push('Removed: ' + applied.removed.join(', '));
  }
  if (Array.isArray(applied.renamed) && applied.renamed.length) {
    parts.push('Renamed: ' + applied.renamed.map(function(r) { return r.from + ' → ' + r.to; }).join(', '));
  }
  if (Array.isArray(applied.valuesSet) && applied.valuesSet.length) {
    parts.push('Set values: ' + applied.valuesSet.map(function(v) { return v.key + '=' + v.value; }).join(', '));
  }
  return parts.join(' • ');
}

// Friendly summary of <edge_changes> applied to outgoing connections.
// Shape: { applied: [{ destTitle, propPath, previousPropPath }], skipped: [...] }
function formatEdgeChangesApplied(edgeChangesApplied) {
  if (!edgeChangesApplied || typeof edgeChangesApplied !== 'object') return '';
  var applied = Array.isArray(edgeChangesApplied.applied) ? edgeChangesApplied.applied : [];
  if (!applied.length) return '';
  return 'Outgoing connections: ' + applied.map(function(a) {
    var label = a.propPath === null ? '(full output)' : a.propPath;
    return (a.destTitle || a.to) + ' → ' + label;
  }).join(', ');
}

function hexToRgb(hex) {
  var raw = String(hex || '').replace('#', '').trim();
  if (raw.length !== 6) return null;
  var r = parseInt(raw.slice(0, 2), 16);
  var g = parseInt(raw.slice(2, 4), 16);
  var b = parseInt(raw.slice(4, 6), 16);
  if (!Number.isFinite(r) || !Number.isFinite(g) || !Number.isFinite(b)) return null;
  return { r: r, g: g, b: b };
}

function rgbToHex(rgb) {
  function clampChannel(value) {
    var safe = Math.max(0, Math.min(255, Math.round(Number(value) || 0)));
    return safe.toString(16).padStart(2, '0');
  }

  return '#' + clampChannel(rgb.r) + clampChannel(rgb.g) + clampChannel(rgb.b);
}

function loadImageDominantBorderColor(url) {
  return new Promise(function(resolve, reject) {
    var imageUrl = String(url || '').trim();
    if (!imageUrl) {
      resolve('');
      return;
    }

    var image = new Image();
    image.crossOrigin = 'anonymous';

    image.onload = function() {
      try {
        var sampleSize = 28;
        var canvas = document.createElement('canvas');
        canvas.width = sampleSize;
        canvas.height = sampleSize;
        var ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Canvas context unavailable');

        ctx.drawImage(image, 0, 0, sampleSize, sampleSize);
        var pixels = ctx.getImageData(0, 0, sampleSize, sampleSize).data;
        var buckets = {};

        for (var i = 0; i < pixels.length; i += 4) {
          var alpha = pixels[i + 3] / 255;
          if (alpha < 0.35) continue;

          var r = pixels[i];
          var g = pixels[i + 1];
          var b = pixels[i + 2];
          var brightness = (0.299 * r) + (0.587 * g) + (0.114 * b);
          if (brightness > 245) continue;

          var max = Math.max(r, g, b);
          var min = Math.min(r, g, b);
          var saturation = max === 0 ? 0 : (max - min) / max;
          var weight = alpha * (0.6 + saturation * 1.4);
          var key = [
            Math.floor(r / 32),
            Math.floor(g / 32),
            Math.floor(b / 32)
          ].join('-');

          if (!buckets[key]) {
            buckets[key] = { weight: 0, red: 0, green: 0, blue: 0 };
          }
          buckets[key].weight += weight;
          buckets[key].red += r * weight;
          buckets[key].green += g * weight;
          buckets[key].blue += b * weight;
        }

        var bestBucket = null;
        Object.keys(buckets).forEach(function(key) {
          if (!bestBucket || buckets[key].weight > bestBucket.weight) {
            bestBucket = buckets[key];
          }
        });

        if (!bestBucket || !bestBucket.weight) throw new Error('No usable pixels found');

        resolve(rgbToHex({
          r: bestBucket.red / bestBucket.weight,
          g: bestBucket.green / bestBucket.weight,
          b: bestBucket.blue / bestBucket.weight
        }));
      } catch (error) {
        reject(error);
      }
    };

    image.onerror = function() {
      reject(new Error('Logo image failed to load'));
    };

    image.src = imageUrl;
  });
}

function loadMonaco() {
  if (monacoLoadPromise) return monacoLoadPromise;
  monacoLoadPromise = new Promise(function(resolve, reject) {
    if (window.monaco) { resolve(window.monaco); return; }
    var script = document.createElement('script');
    script.src = MONACO_CDN + '/loader.js';
    script.onload = function() {
      window.require.config({ paths: { vs: MONACO_CDN } });
      window.require(['vs/editor/editor.main'], function() {
        resolve(window.monaco);
      });
    };
    script.onerror = function() {
      monacoLoadPromise = null;
      reject(new Error('Monaco failed to load'));
    };
    document.head.appendChild(script);
  });
  return monacoLoadPromise;
}

function MonacoCodeEditor(props) {
  var value = props.value;
  var onChange = props.onChange;
  var readOnly = !!props.readOnly;

  var containerRef = useRef(null);
  var editorRef = useRef(null);
  var internalChangeRef = useRef(false);
  var [isLoading, setIsLoading] = useState(true);
  var [loadError, setLoadError] = useState(false);

  useEffect(function() {
    var isMounted = true;

    loadMonaco().then(function(monaco) {
      if (!isMounted || !containerRef.current) return;

      var editor = monaco.editor.create(containerRef.current, {
        value: value,
        language: 'javascript',
        theme: 'vs',
        fontSize: 13,
        fontFamily: "'SF Mono', 'Fira Code', 'Cascadia Code', Consolas, monospace",
        lineHeight: 20,
        minimap: { enabled: false },
        scrollBeyondLastLine: false,
        automaticLayout: true,
        tabSize: 2,
        insertSpaces: true,
        wordWrap: 'off',
        renderLineHighlight: 'line',
        lineNumbers: 'on',
        glyphMargin: false,
        folding: true,
        lineNumbersMinChars: 3,
        padding: { top: 12, bottom: 12 },
        scrollbar: { verticalScrollbarSize: 6, horizontalScrollbarSize: 6 },
        readOnly: readOnly,
        domReadOnly: readOnly
      });

      editorRef.current = editor;
      setIsLoading(false);

      editor.onDidChangeModelContent(function() {
        if (!isMounted) return;
        internalChangeRef.current = true;
        onChange(editor.getValue());
      });
    }).catch(function() {
      if (isMounted) setLoadError(true);
    });

    return function() {
      isMounted = false;
      if (editorRef.current) {
        editorRef.current.dispose();
        editorRef.current = null;
      }
    };
  }, []);

  useEffect(function() {
    var editor = editorRef.current;
    if (!editor) return;
    if (internalChangeRef.current) {
      internalChangeRef.current = false;
      return;
    }
    if (editor.getValue() !== value) {
      var pos = editor.getPosition();
      editor.setValue(value);
      if (pos) editor.setPosition(pos);
    }
  }, [value]);

  if (loadError) {
    return h('textarea', {
      className: 'coe-code-fallback',
      value: value,
      onChange: function(e) { onChange(e.target.value); },
      spellCheck: false,
      autoComplete: 'off'
    });
  }

  return h('div', { className: 'coe-monaco-wrap' },
    isLoading
      ? h('div', { className: 'coe-monaco-loading' }, 'Loading editor...')
      : null,
    h('div', {
      ref: containerRef,
      className: 'coe-monaco-container',
      style: { opacity: isLoading ? 0 : 1 }
    })
  );
}

// ── Icons ────────────────────────────────────────────────────────────────────

function SparkleIcon() {
  return h('svg', { width: 13, height: 13, viewBox: '0 0 24 24', fill: 'currentColor' },
    h('path', { d: 'M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z' })
  );
}

function SendIcon() {
  return h('svg', { width: 15, height: 15, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2.2, strokeLinecap: 'round', strokeLinejoin: 'round' },
    h('line', { x1: 22, y1: 2, x2: 11, y2: 13 }),
    h('polygon', { points: '22 2 15 22 11 13 2 9 22 2' })
  );
}

function StopIcon() {
  return h('svg', { width: 12, height: 12, viewBox: '0 0 24 24', fill: 'currentColor' },
    h('rect', { x: 5, y: 5, width: 14, height: 14, rx: 2 })
  );
}

function AttachIcon() {
  return h('svg', { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
    h('path', { d: 'M21.44 11.05 12.25 20.24a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48' })
  );
}

function CloseIcon() {
  return h('svg', { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2 },
    h('line', { x1: 18, y1: 6, x2: 6, y2: 18 }),
    h('line', { x1: 6, y1: 6, x2: 18, y2: 18 })
  );
}

function SettingsFields(props) {
  var showName = !!props.showName;
  var name = props.name || '';
  var onNameChange = props.onNameChange || function() {};
  var logoUrl = props.logoUrl || '';
  var onLogoChange = props.onLogoChange || function() {};
  var logoError = !!props.logoError;
  var setLogoError = props.setLogoError || function() {};
  var borderColor = props.borderColor || '';
  var onBorderColorChange = props.onBorderColorChange || function() {};
  var onLogoPreviewLoad = props.onLogoPreviewLoad || function() {};
  var colorOptions = NODE_BORDER_COLOR_OPTIONS.slice();
  var hasSelectedPreset = colorOptions.some(function(option) {
    return option.value && String(option.value).toLowerCase() === String(borderColor || '').toLowerCase();
  });

  if (borderColor && !hasSelectedPreset) {
    colorOptions = [{ value: borderColor, label: 'Detected' }].concat(colorOptions);
  }

  return h('div', { className: 'coe-settings-panel' },
    showName
      ? h('div', { className: 'coe-field' },
          h('label', { className: 'coe-label' }, 'Name'),
          h('input', {
            className: 'coe-input',
            type: 'text',
            placeholder: 'e.g. Send Email',
            value: name,
            onChange: function(e) { onNameChange(e.target.value); },
            autoFocus: true
          })
        )
      : null,
    h('div', { className: 'coe-field' },
      h('label', { className: 'coe-label' }, 'Border color'),
      h('div', { className: 'coe-color-swatches', role: 'radiogroup', 'aria-label': 'Node border color' },
        colorOptions.map(function(option) {
          var isActive = String(borderColor || '').toLowerCase() === String(option.value || '').toLowerCase();
          return h('button', {
            key: option.label + '-' + String(option.value || 'default'),
            type: 'button',
            className: 'coe-color-swatch' + (isActive ? ' coe-color-swatch-active' : '') + (!option.value ? ' coe-color-swatch-default' : ''),
            style: option.value ? { '--swatch-color': option.value } : undefined,
            title: option.label,
            'aria-label': option.label,
            'aria-pressed': isActive ? 'true' : 'false',
            onClick: function() { onBorderColorChange(option.value); }
          }, h('span', { className: 'coe-color-swatch-dot', 'aria-hidden': 'true' }));
        })
      )
    ),
    h('div', { className: 'coe-field' },
      h('label', { className: 'coe-label' }, 'Logo URL'),
      h('div', { className: 'coe-logo-row' },
        h('input', {
          className: 'coe-input',
          type: 'text',
          placeholder: 'https://example.com/icon.png',
          value: logoUrl,
          onChange: onLogoChange
        }),
        logoUrl && !logoError
          ? h('img', {
              className: 'coe-logo-preview',
              src: logoUrl,
              alt: 'preview',
              onLoad: function() { onLogoPreviewLoad(logoUrl); },
              onError: function() { setLogoError(true); }
            })
          : null
      )
    )
  );
}

// ── Generate bar ─────────────────────────────────────────────────────────────

function GenerateBar(props) {
  var onResult = props.onResult;
  var onGenerate = props.onGenerate;
  var placeholder = props.placeholder || 'Describe what this node should do... (e.g. "fetch weather data from an API")';
  var buttonLabel = props.buttonLabel || 'Generate';
  var shouldAutoFocus = !!props.autoFocus;
  var [prompt, setPrompt] = useState('');
  var [loading, setLoading] = useState(false);
  var [error, setError] = useState(null);

  function handleGenerate() {
    if (!prompt.trim() || loading) return;
    setLoading(true);
    setError(null);
    var generatePromise = typeof onGenerate === 'function'
      ? Promise.resolve(onGenerate(prompt.trim()))
      : request('/custom-objects/generate', { method: 'POST', body: { prompt: prompt.trim() } });

    generatePromise
      .then(function(data) {
        onResult(data, prompt.trim());
        setPrompt('');
      })
      .catch(function(err) {
        setError(err && err.message ? err.message : 'Generation failed');
      })
      .finally(function() {
        setLoading(false);
      });
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleGenerate();
    }
  }

  return h('div', { className: 'coe-generate-bar' },
    h('div', { className: 'coe-generate-row' },
      h('span', { className: 'coe-generate-icon' }, h(SparkleIcon)),
      h('textarea', {
        className: 'coe-generate-textarea',
        placeholder: placeholder,
        value: prompt,
        rows: 1,
        autoFocus: shouldAutoFocus,
        onChange: function(e) { setPrompt(e.target.value); },
        onKeyDown: handleKeyDown,
        disabled: loading,
        spellCheck: false
      }),
      h('button', {
        className: 'coe-btn-generate' + (loading ? ' coe-btn-generate--loading' : ''),
        onClick: handleGenerate,
        disabled: !prompt.trim() || loading
      }, loading ? 'Generating...' : buttonLabel)
    ),
    error
      ? h('div', { className: 'coe-generate-error' }, error)
      : null
  );
}

// ── Markdown renderer ─────────────────────────────────────────────────────────

function renderMarkdown(text) {
  var lines = String(text || '').split('\n');
  var result = [];
  var i = 0;

  while (i < lines.length) {
    var line = lines[i];

    // Numbered list item
    var olMatch = line.match(/^(\d+)\.\s+(.+)/);
    if (olMatch) {
      var olItems = [];
      while (i < lines.length) {
        var m = lines[i].match(/^\d+\.\s+(.+)/);
        if (!m) break;
        olItems.push(h('li', { key: i }, inlineMarkdown(m[1])));
        i++;
      }
      result.push(h('ol', { key: 'ol-' + result.length }, olItems));
      continue;
    }

    // Bullet list item
    var ulMatch = line.match(/^[-*]\s+(.+)/);
    if (ulMatch) {
      var ulItems = [];
      while (i < lines.length) {
        var um = lines[i].match(/^[-*]\s+(.+)/);
        if (!um) break;
        ulItems.push(h('li', { key: i }, inlineMarkdown(um[1])));
        i++;
      }
      result.push(h('ul', { key: 'ul-' + result.length }, ulItems));
      continue;
    }

    // Empty line → spacer
    if (!line.trim()) {
      result.push(h('div', { key: 'sp-' + i, className: 'coe-ask-spacer' }));
      i++;
      continue;
    }

    result.push(h('p', { key: 'p-' + i }, inlineMarkdown(line)));
    i++;
  }

  return result;
}

function inlineMarkdown(text) {
  // Split on **bold**, *italic*, `code`
  var parts = String(text || '').split(/(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g);
  return parts.map(function(part, i) {
    if (part.startsWith('**') && part.endsWith('**')) {
      return h('strong', { key: i }, part.slice(2, -2));
    }
    if (part.startsWith('*') && part.endsWith('*')) {
      return h('em', { key: i }, part.slice(1, -1));
    }
    if (part.startsWith('`') && part.endsWith('`')) {
      return h('mark', { key: i, className: 'coe-ask-inline-mark' }, part.slice(1, -1));
    }
    return part;
  });
}

// ── Ask panel ─────────────────────────────────────────────────────────────────

function AskPanel(props) {
  var getCode = props.getCode;
  var onCodeChange = props.onCodeChange;
  var onHyperparamsChange = props.onHyperparamsChange;
  var initialData = props.initialData || {};
  var messages = props.messages;
  var setMessages = props.setMessages;
  var pendingAsk = props.pendingAsk;
  var onPendingAskConsumed = props.onPendingAskConsumed;
  var onPendingAskComplete = props.onPendingAskComplete;
  // When opened by Doctor or Autopilot the fix runs automatically — hide Q&A
  // so the user can watch but not interrupt with a conflicting message.
  var autoFixSource = props.autoFixSource || null;
  var [question, setQuestion] = useState('');
  var [loading, setLoading] = useState(false);
  var [contextExpanded, setContextExpanded] = useState(false);
  var [promptHistoryExpanded, setPromptHistoryExpanded] = useState(false);
  var [probeSelections, setProbeSelections] = useState({});
  // Pending images to send with the next ask. Each entry is { id, dataUrl, mediaType, name }.
  // dataUrl is the base64-encoded "data:image/png;base64,..." string. Cleared on send.
  var [pendingImages, setPendingImages] = useState([]);
  var [isDragging, setIsDragging] = useState(false);
  var fileInputRef = useRef(null);
  var bottomRef = useRef(null);
  // Synchronous guard against double-fires. React's `loading` state isn't
  // committed until the next render, so two handlers firing in the same tick
  // (e.g. Enter + click, or a pendingAsk effect re-running) both read
  // loading=false and both append a user + assistant pair. This ref flips
  // synchronously so the second call bails.
  var inFlightRef = useRef(false);
  // Remember the last pendingAsk value we consumed so the effect below never
  // re-fires for the same pendingAsk even if the parent is slow to clear it.
  var consumedPendingAskRef = useRef(null);
  // Holds the abort() function returned by requestStream while an ask is in
  // flight, so the parent (or this panel) can cancel mid-stream.
  var abortRef = useRef(null);
  var onAskingChange = typeof props.onAskingChange === 'function' ? props.onAskingChange : null;
  // Parent passes a mutable ref so it can call cancelAsk() from its own
  // close-confirmation handler without prop-drilling event hooks.
  var cancelRef = props.cancelRef || null;
  var nodeGenerationPrompt = props.generationPrompt || initialData.generationPrompt || '';
  var nodeChangeHistory = Array.isArray(props.changeHistory) ? props.changeHistory : (Array.isArray(initialData.changeHistory) ? initialData.changeHistory : []);

  function cancelAsk() {
    if (abortRef.current) {
      try { abortRef.current(); } catch (e) { /* noop */ }
      abortRef.current = null;
    }
    inFlightRef.current = false;
    setLoading(false);
    if (onAskingChange) onAskingChange(false);
    setMessages(function(prev) {
      var changed = false;
      var next = prev.map(function(m) {
        if (m && m.role === 'assistant' && m.streaming) {
          changed = true;
          return Object.assign({}, m, {
            streaming: false,
            text: (m.streamingText && String(m.streamingText).trim()) || m.text || 'Stopped.',
            cancelled: true
          });
        }
        return m;
      });
      return changed ? next : prev;
    });
  }

  // Expose cancelAsk() to the parent via the shared ref.
  useEffect(function() {
    if (cancelRef) cancelRef.current = cancelAsk;
    return function() { if (cancelRef) cancelRef.current = null; };
  }, []);

  // Tell the parent whenever loading flips.
  useEffect(function() {
    if (onAskingChange) onAskingChange(!!loading);
  }, [loading]);
  var outputContextText = (function() {
    if (!Object.prototype.hasOwnProperty.call(initialData, 'lastOutput')) return null;
    var raw = initialData.lastOutput;
    var minimized = (raw !== null && typeof raw === 'object') ? minimizeArrays(raw) : raw;
    var staleness = formatLastOutputStaleness(initialData.lastOutputAt);
    var body;
    try { body = JSON.stringify(minimized, null, 2); } catch(e) { body = String(minimized); }
    return staleness ? (staleness + '\n' + body) : body;
  })();

  useEffect(function() {
    if (messages.length !== 0) return;
    var seeded = [];
    // When autopilot opened this panel to fix a flagged node, lead with an
    // info banner showing the checker's reason. The user sees "why we're
    // here" before the AI retries the fix.
    if (props.autoFixReason && String(props.autoFixReason).trim()) {
      seeded.push({ role: 'autofix-info', text: String(props.autoFixReason).trim() });
    }
    if (outputContextText !== null) {
      seeded.push({ role: 'context', text: outputContextText });
    }
    if (seeded.length > 0) setMessages(seeded);
  }, []);

  useEffect(function() {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  useEffect(function() {
    if (pendingAsk && !loading && consumedPendingAskRef.current !== pendingAsk) {
      consumedPendingAskRef.current = pendingAsk;
      doAsk(pendingAsk, true);
      if (typeof onPendingAskConsumed === 'function') onPendingAskConsumed();
    }
    if (!pendingAsk) consumedPendingAskRef.current = null;
  }, [pendingAsk]);

  function doAsk(q, isPending) {
    // Allow image-only sends — q may be '' when only images are attached.
    if (!q && pendingImages.length === 0) return;
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    var code = typeof getCode === 'function' ? getCode() : '';
    // Snapshot images at send time, then clear so the next ask starts fresh.
    var imagesForThisAsk = pendingImages.slice();
    setPendingImages([]);
    setQuestion('');
    setLoading(true);
    // Each ask gets a unique id so streaming handlers can find the right
    // assistant row by identity instead of by index. Index-based lookup
    // drifted when doAsk was called twice or when messages shifted, leaving
    // ghost "working..." rows. Id-based updates make that impossible.
    var askId = 'ask-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8);
    var askStartedAt = Date.now();
    var priorMessages = (messages || [])
      // UI-only banner — never sent to the AI as part of the chat history.
      .filter(function(m) { return m && m.role !== 'autofix-info'; })
      .map(function(m) {
        if (m.role === 'context') return { role: 'user', content: 'Node output context:\n' + m.text };
        // Preserve images on prior user turns so the model still "sees" them
        // on follow-ups. Backend reattaches them as multimodal blocks.
        var hasImages = m.role === 'user' && Array.isArray(m.images) && m.images.length > 0;
        if (hasImages) {
          return {
            role: 'user',
            content: m.text || '',
            images: m.images.map(function(img) { return { dataUrl: img.dataUrl, mediaType: img.mediaType }; })
          };
        }
        return { role: m.role === 'assistant' ? 'assistant' : 'user', content: m.text };
      });
    // Single atomic append of both rows. Idempotency guard: if a row with
    // this askId already exists (e.g. a stray double-invoke), don't duplicate.
    setMessages(function(prev) {
      for (var i = 0; i < prev.length; i += 1) {
        if (prev[i] && prev[i].askId === askId) return prev;
      }
      return prev.concat(
        { role: 'user', text: q, images: imagesForThisAsk, askId: askId },
        { role: 'assistant', text: '', streaming: true, activity: [], streamingText: '', startedAt: askStartedAt, askId: askId }
      );
    });
    function updateAssistantById(updater) {
      setMessages(function(prev) {
        var updated = prev.slice();
        for (var i = 0; i < updated.length; i += 1) {
          var m = updated[i];
          if (m && m.role === 'assistant' && m.askId === askId) {
            updated[i] = updater(m);
            return updated;
          }
        }
        return prev; // assistant row gone — no-op, never append a new one
      });
    }
    // Shrinks an arbitrary value through minimizeArrays, but is a no-op for
    // null/undefined so we don't replace "absent" with "{}".
    function shrink(value) {
      if (value === null || value === undefined) return value;
      return minimizeArrays(value);
    }
    // Prior messages can carry images on user turns — minimize only the text
    // content so dataUrl previews don't get truncated to unusable thumbnails.
    var shrunkPriorMessages = priorMessages.map(function(m) {
      var out = { role: m.role, content: typeof m.content === 'string' ? m.content : minimizeArrays(m.content) };
      if (m.images) out.images = m.images;
      return out;
    });
    var askBody = {
      workflowId: props.workflowId || '',
      nodeId: initialData.id || '',
      // Live graph snapshot for downstream-destination context. Sent inline so
      // AskCode works for unsaved workflows (no DB record yet). Slim shape —
      // only the fields buildDownstreamContext reads. Not minimized — backend
      // walks structural fields (edges, connections) that minimization would
      // truncate.
      workflowGraph: (initialData.workflowGraph && typeof initialData.workflowGraph === 'object') ? initialData.workflowGraph : null,
      code: code,
      question: q,
      messages: shrunkPriorMessages,
      variables: shrink(initialData.availableVariables || {}),
      variableTypes: (initialData.variableTypesByName && typeof initialData.variableTypesByName === 'object') ? initialData.variableTypesByName : {},
      lastInput: Object.prototype.hasOwnProperty.call(initialData, 'lastInput') ? shrink(initialData.lastInput) : undefined,
      lastOutput: Object.prototype.hasOwnProperty.call(initialData, 'lastOutput') ? shrink(initialData.lastOutput) : undefined,
      nodeTitle: String(initialData.title || ''),
      startParams: initialData.startParams || null,
      hyperparameters: Array.isArray(initialData.hyperparameters) ? initialData.hyperparameters : [],
      hyperparams: (initialData.paramValues && typeof initialData.paramValues === 'object') ? initialData.paramValues : {},
      hyperparamsResolved: shrink(resolveParamValues(
        initialData.paramValues || {},
        initialData.availableVariables || {},
        initialData.nodeOutputByTitle || {}
      )),
      generationPrompt: nodeGenerationPrompt,
      changeHistory: nodeChangeHistory,
      // Pasted/dropped/uploaded images sent with this turn. Backend builds a
      // multimodal user message and routes the chat call to a vision model.
      images: imagesForThisAsk.map(function(img) {
        return { dataUrl: img.dataUrl, mediaType: img.mediaType };
      })
    };

    // Extract the live <think> content (partial or complete) so we can stream it to the UI.
    function extractStreamingThink(fullText) {
      var closed = fullText.match(/<think>([\s\S]*?)<\/think>/i);
      if (closed) return closed[1].trim();
      var open = fullText.match(/<think>([\s\S]*)/i);
      if (open) return open[1].trim();
      return '';
    }

    // Incrementally extract <answer> content (or a status label) from the streaming fullText.
    function extractStreamingAnswer(fullText) {
      var answerMatch = fullText.match(/<answer>([\s\S]*?)<\/answer>/i);
      if (answerMatch) return answerMatch[1].trim();
      // Partial: answer tag opened but not closed yet
      var partialMatch = fullText.match(/<answer>([\s\S]*)/i);
      if (partialMatch) return partialMatch[1];
      // Before answer tag: show status if inside thinking tags
      var typeMatch = fullText.match(/<type>([\s\S]*?)<\/type>/i);
      var detectedType = typeMatch ? typeMatch[1].trim() : '';
      if (detectedType === 'change') {
        if (/<think>/i.test(fullText) && !/<\/think>/i.test(fullText)) return 'Thinking...';
        if (/<final_plan>/i.test(fullText) && !/<\/final_plan>/i.test(fullText)) return 'Planning changes...';
        if (/<code>/i.test(fullText) && !/<\/code>/i.test(fullText)) return 'Writing code...';
        if (/<\/think>/i.test(fullText) && !/<answer>/i.test(fullText)) return 'Preparing answer...';
      }
      if (detectedType === 'probe') {
        if (/<think>/i.test(fullText) && !/<\/think>/i.test(fullText)) return 'Analyzing your question...';
        if (/<probe\s/i.test(fullText)) return 'Planning investigation...';
        if (!/<\/answer>/i.test(fullText)) return 'Investigating...';
      }
      return '';
    }

    abortRef.current = requestStream('/custom-objects/ask', { method: 'POST', body: askBody }, {
      onEvent: function(payload) {
        if (payload.type === 'chunk') {
          var fullText = payload.fullText || '';
          var partial = extractStreamingAnswer(fullText);
          var think = extractStreamingThink(fullText);
          updateAssistantById(function(cur) {
            return Object.assign({}, cur, {
              role: 'assistant',
              streaming: true,
              streamingText: partial,
              streamingThink: think,
              startedAt: cur.startedAt || askStartedAt,
              activity: Array.isArray(cur.activity) ? cur.activity : [],
              askId: askId
            });
          });
        } else if (payload.type === 'status') {
          updateAssistantById(function(cur) {
            var prevActivity = Array.isArray(cur.activity) ? cur.activity.slice() : [];
            for (var ai = 0; ai < prevActivity.length; ai++) {
              if (prevActivity[ai].status === 'current') {
                prevActivity[ai] = Object.assign({}, prevActivity[ai], { status: 'done' });
              }
            }
            var newText = String(payload.text || '').trim();
            var last = prevActivity[prevActivity.length - 1];
            if (newText && (!last || last.text !== newText)) {
              prevActivity.push({ text: newText, status: 'current', t: Date.now() });
            }
            return Object.assign({}, cur, {
              role: 'assistant',
              streaming: true,
              activity: prevActivity,
              startedAt: cur.startedAt || askStartedAt,
              askId: askId
            });
          });
        } else if (payload.type === 'probe_result') {
          updateAssistantById(function(cur) {
            var prevProbes = Array.isArray(cur.probes) ? cur.probes.slice() : [];
            prevProbes.push(payload.data || {});
            return Object.assign({}, cur, {
              role: 'assistant',
              streaming: true,
              probes: prevProbes,
              activity: Array.isArray(cur.activity) ? cur.activity : [],
              startedAt: cur.startedAt || askStartedAt,
              askId: askId
            });
          });
        } else if (payload.type === 'done') {
          var data = payload.data || {};
          // A "change" turn now has THREE possible edit surfaces: code,
          // hyperparams, and outgoing-edge propPath. Any one of them is enough
          // to count this as an applied change so the UI shows "Changes applied".
          var hasCodeChange = data.type === 'change' && !!data.code;
          var hasEdgeChange = data.type === 'change' && Array.isArray(data.connectionUpdates) && data.connectionUpdates.length > 0;
          var isChange = hasCodeChange || hasEdgeChange;
          var isProbe = (data.type === 'probe' || data.type === 'question') && data.questions && data.questions.length > 0;
          var hpUpdated = hasCodeChange && Array.isArray(data.hyperparameters);
          if (hasCodeChange && typeof onCodeChange === 'function') {
            onCodeChange(
              data.code,
              data.changeDescription || null,
              hpUpdated ? data.hyperparameters : null,
              hpUpdated ? (data.paramValues || null) : null
            );
          }
          if (hpUpdated && typeof onHyperparamsChange === 'function') {
            onHyperparamsChange(data.hyperparameters, data.paramValues || null);
          }
          if (hasEdgeChange && typeof props.onConnectionUpdates === 'function') {
            props.onConnectionUpdates(data.connectionUpdates, data.changeDescription || null);
          }
          if (isProbe) {
            setProbeSelections({});
          }
          updateAssistantById(function(cur) {
            var finalProbes = Array.isArray(data.probes) && data.probes.length > 0
              ? data.probes
              : (Array.isArray(cur && cur.probes) ? cur.probes : []);
            var finalActivity = Array.isArray(cur && cur.activity)
              ? cur.activity.map(function(it) {
                  return it && it.status === 'current' ? Object.assign({}, it, { status: 'done' }) : it;
                })
              : [];
            return {
              role: 'assistant',
              streaming: false,
              text: data.answer || '',
              applied: isChange,
              hyperparamsApplied: hpUpdated ? (data.hyperparamsApplied || null) : null,
              edgeChangesApplied: hasEdgeChange ? (data.edgeChangesApplied || null) : null,
              questions: isProbe ? data.questions : null,
              activity: finalActivity,
              probes: finalProbes,
              startedAt: cur && cur.startedAt ? cur.startedAt : askStartedAt,
              askId: askId
            };
          });
          if (isPending && typeof onPendingAskComplete === 'function') {
            // Autopilot resume contract: pass an object so the caller can
            // distinguish a code-only fix (newCode set), an edge-only fix
            // (hasEdgeChanges set, no newCode), and a no-op (both falsy).
            onPendingAskComplete({
              code: hasCodeChange ? data.code : null,
              hasEdgeChanges: !!hasEdgeChange
            });
          }
          setLoading(false);
          inFlightRef.current = false;
        } else if (payload.type === 'error') {
          updateAssistantById(function() {
            return { role: 'assistant', text: 'Something went wrong. Please try again.', askId: askId };
          });
          if (isPending && typeof onPendingAskComplete === 'function') {
            onPendingAskComplete({ code: null, hasEdgeChanges: false });
          }
          setLoading(false);
          inFlightRef.current = false;
        }
      },
      onError: function() {
        updateAssistantById(function() {
          return { role: 'assistant', text: 'Something went wrong. Please try again.', askId: askId };
        });
        if (isPending && typeof onPendingAskComplete === 'function') {
          onPendingAskComplete({ code: null, hasEdgeChanges: false });
        }
        setLoading(false);
        inFlightRef.current = false;
      },
      onClose: function() {
        setLoading(false);
        inFlightRef.current = false;
      }
    });
  }

  function submitProbeAnswers(questions) {
    var parts = [];
    for (var i = 0; i < questions.length; i++) {
      var q = questions[i];
      var ans = probeSelections[i];
      if (ans && String(ans).trim()) {
        parts.push(q.label + ': ' + String(ans).trim());
      }
    }
    if (parts.length > 0) {
      setProbeSelections({});
      doAsk(parts.join('\n'));
    }
  }

  function handleAsk() {
    var q = question.trim();
    // Allow sending image-only messages (q can be empty if there are images).
    if (!q && pendingImages.length === 0) return;
    if (loading || inFlightRef.current) cancelAsk();
    doAsk(q);
  }

  // Image attach helpers — paste, drop, and click-to-upload all funnel here.
  // Reads each image File as a base64 data URL and pushes it into pendingImages.
  // Skips non-images, oversize files (>10MB after base64 ≈ 7.5MB raw), and dupes.
  var IMAGE_MAX_BYTES = 7.5 * 1024 * 1024;
  function addImageFiles(fileList) {
    var files = Array.from(fileList || []).filter(function(f) {
      return f && typeof f.type === 'string' && f.type.indexOf('image/') === 0;
    });
    if (files.length === 0) return;
    files.forEach(function(file) {
      if (file.size > IMAGE_MAX_BYTES) {
        console.warn('[askcode] skipping image over 7.5MB:', file.name, file.size);
        return;
      }
      var reader = new FileReader();
      reader.onload = function() {
        var dataUrl = String(reader.result || '');
        if (!dataUrl.startsWith('data:image/')) return;
        setPendingImages(function(prev) {
          // Dedupe by dataUrl prefix (covers "same image pasted twice").
          for (var i = 0; i < prev.length; i++) {
            if (prev[i].dataUrl === dataUrl) return prev;
          }
          return prev.concat([{
            id: 'img-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8),
            dataUrl: dataUrl,
            mediaType: file.type,
            name: file.name || 'pasted-image'
          }]);
        });
      };
      reader.readAsDataURL(file);
    });
  }

  function removePendingImage(id) {
    setPendingImages(function(prev) {
      return prev.filter(function(img) { return img.id !== id; });
    });
  }

  function handlePasteOnInput(e) {
    var items = e.clipboardData && e.clipboardData.items;
    if (!items) return;
    var files = [];
    for (var i = 0; i < items.length; i++) {
      if (items[i].kind === 'file') {
        var f = items[i].getAsFile();
        if (f) files.push(f);
      }
    }
    if (files.length > 0) {
      e.preventDefault();
      addImageFiles(files);
    }
  }

  function handleDropOnInput(e) {
    e.preventDefault();
    setIsDragging(false);
    var dt = e.dataTransfer;
    if (dt && dt.files && dt.files.length > 0) {
      addImageFiles(dt.files);
    }
  }

  function handleDragOverOnInput(e) {
    e.preventDefault();
    if (!isDragging) setIsDragging(true);
  }

  function handleDragLeaveOnInput(e) {
    // Only flip off when leaving the wrap entirely, not when crossing inner children.
    if (e.currentTarget === e.target) setIsDragging(false);
  }

  function handleAttachClick() {
    if (fileInputRef.current) fileInputRef.current.click();
  }

  function handleFileInputChange(e) {
    addImageFiles(e.target.files);
    // Reset so picking the same file twice in a row still triggers onChange.
    e.target.value = '';
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAsk();
    }
  }

  var hasInputContext = outputContextText !== null;
  var userMsgCount = (messages || []).filter(function(m) { return m && m.role === 'user'; }).length;
  var showQuickActions = !loading && userMsgCount === 0;
  var quickActions = [{ label: 'How does this step work?', prompt: 'How does this step work?' }];
  if (hasInputContext) {
    quickActions.push({ label: 'Fix any issue', prompt: 'Look at what this step produced last time and fix anything that looks wrong.' });
  }

  return h('div', { className: 'coe-ask-panel' },
    (nodeGenerationPrompt || nodeChangeHistory.length > 0)
      ? h('div', { className: 'coe-ask-prompt-history' },
          h('button', {
            className: 'coe-ask-prompt-history-toggle',
            onClick: function() { setPromptHistoryExpanded(function(v) { return !v; }); }
          },
            h('span', { className: 'coe-ask-context-toggle' }, promptHistoryExpanded ? '\u25be' : '\u25b8'),
            ' Prompt & Changes'
          ),
          promptHistoryExpanded
            ? h('div', { className: 'coe-ask-prompt-history-content' },
                nodeGenerationPrompt
                  ? h('div', { className: 'coe-ask-prompt-history-section' },
                      h('div', { className: 'coe-ask-prompt-history-label' }, 'Original Prompt:'),
                      h('div', { className: 'coe-ask-prompt-history-text' }, nodeGenerationPrompt)
                    )
                  : null,
                nodeChangeHistory.length > 0
                  ? h('div', { className: 'coe-ask-prompt-history-section' },
                      h('div', { className: 'coe-ask-prompt-history-label' }, 'Changes:'),
                      h('ul', { className: 'coe-ask-prompt-history-list' },
                        nodeChangeHistory.map(function(entry, idx) {
                          return h('li', { key: idx, className: 'coe-ask-prompt-history-item' }, entry);
                        })
                      )
                    )
                  : null
              )
            : null
        )
      : null,
    h('div', { className: 'coe-ask-messages' },
      messages.length === 0
        ? h('div', { className: 'coe-ask-empty' },
            h('div', { className: 'coe-ask-bot' },
              h('span', { className: 'coe-ask-bot-eye' }),
              h('span', { className: 'coe-ask-bot-eye' })
            ),
            h('div', { className: 'coe-ask-empty-title' }, 'Ask about this step'),
            h('div', { className: 'coe-ask-empty-hint' }, 'Ask a question or describe a change — e.g. "What does this do?" or "Only show active products"')
          )
        : messages.map(function(msg, i) {
            if (msg.role === 'autofix-info') {
              return h('div', { key: i, className: 'coe-ask-autofix-info' },
                h('div', { className: 'coe-ask-autofix-info-label' }, 'Autopilot flagged this step'),
                h('div', { className: 'coe-ask-autofix-info-body' }, msg.text)
              );
            }
            if (msg.role === 'context') {
              return h('div', { key: i, className: 'coe-ask-context-block' },
                h('button', {
                  className: 'coe-ask-context-label',
                  onClick: function() { setContextExpanded(function(v) { return !v; }); }
                },
                  h('span', { className: 'coe-ask-context-toggle' }, contextExpanded ? '\u25be' : '\u25b8'),
                  '\u29f8\u29f9 Last result'
                ),
                contextExpanded ? h('pre', { className: 'coe-ask-context-pre' }, msg.text) : null
              );
            }
            // Render probe questions for assistant messages — one at a time
            if (msg.role === 'assistant' && msg.questions && msg.questions.length > 0) {
              var isLastMsg = i === messages.length - 1;
              var interactive = isLastMsg && !loading;
              var questionElements = [];
              questionElements.push(h('div', { key: 'ans', className: 'coe-ask-msg-text' }, renderMarkdown(msg.text)));

              if (interactive) {
                // Find the first unanswered question index
                var firstUnanswered = -1;
                for (var qi = 0; qi < msg.questions.length; qi++) {
                  if (!probeSelections[qi] || !String(probeSelections[qi]).trim()) {
                    firstUnanswered = qi;
                    break;
                  }
                }

                // Show answered questions as compact items
                for (var ai = 0; ai < msg.questions.length; ai++) {
                  var aq = msg.questions[ai];
                  var aSelected = probeSelections[ai] || null;
                  if (aSelected && String(aSelected).trim()) {
                    var aCaptured = ai;
                    questionElements.push(h('div', { key: 'q-' + ai, className: 'coe-ask-question-block' },
                      h('div', { className: 'coe-ask-question-label' }, aq.label),
                      h('div', { className: 'coe-ask-selected-answer' },
                        h('span', null, aSelected),
                        h('button', {
                          className: 'coe-ask-change-btn',
                          onClick: (function(idx) { return function() {
                            var copy = Object.assign({}, probeSelections);
                            delete copy[idx];
                            setProbeSelections(copy);
                          }; })(aCaptured)
                        }, 'Change')
                      )
                    ));
                  } else if (ai === firstUnanswered) {
                    // Show only the current unanswered question
                    var cq = msg.questions[ai];
                    var cCaptured = ai;
                    if (cq.options && cq.options.length > 0) {
                      questionElements.push(h('div', { key: 'q-' + ai, className: 'coe-ask-question-block coe-ask-question-active' },
                        h('div', { className: 'coe-ask-question-label' }, cq.label),
                        h('div', { className: 'coe-ask-question-options' },
                          cq.options.map(function(opt, oIdx) {
                            return h('button', {
                              key: oIdx,
                              className: 'coe-ask-option-btn',
                              onClick: (function(idx, val) { return function() {
                                var copy = Object.assign({}, probeSelections);
                                copy[idx] = val;
                                setProbeSelections(copy);
                              }; })(cCaptured, opt)
                            }, opt);
                          })
                        )
                      ));
                    } else {
                      // Open-ended input
                      var cVal = probeSelections[ai] || '';
                      questionElements.push(h('div', { key: 'q-' + ai, className: 'coe-ask-question-block coe-ask-question-active' },
                        h('div', { className: 'coe-ask-question-label' }, cq.label),
                        h('textarea', {
                          className: 'coe-ask-question-textarea',
                          placeholder: 'Type your answer...',
                          value: cVal,
                          rows: 2,
                          onChange: (function(idx) { return function(e) {
                            var copy = Object.assign({}, probeSelections);
                            copy[idx] = e.target.value;
                            setProbeSelections(copy);
                          }; })(cCaptured)
                        })
                      ));
                    }
                    // Don't show remaining questions — only one at a time
                    break;
                  }
                }

                // Show submit button only when ALL questions are answered
                var allAnswered = msg.questions.every(function(_, idx) {
                  return probeSelections[idx] && String(probeSelections[idx]).trim();
                });
                if (allAnswered) {
                  var capturedQuestions = msg.questions;
                  questionElements.push(h('div', { key: 'submit-row', className: 'coe-ask-submit-row' },
                    h('button', {
                      className: 'coe-ask-submit-btn',
                      onClick: function() { submitProbeAnswers(capturedQuestions); }
                    }, 'Submit Answers')
                  ));
                }
              } else {
                // Non-interactive (past messages): show all questions with selected answers
                msg.questions.forEach(function(q, qIdx) {
                  var sel = probeSelections[qIdx];
                  questionElements.push(h('div', { key: 'q-' + qIdx, className: 'coe-ask-question-block' },
                    h('div', { className: 'coe-ask-question-label' }, q.label),
                    sel
                      ? h('div', { className: 'coe-ask-selected-answer' }, h('span', null, sel))
                      : (q.options && q.options.length > 0)
                        ? h('div', { className: 'coe-ask-question-options-static' },
                            q.options.map(function(opt, oIdx) {
                              return h('div', { key: oIdx, className: 'coe-ask-option-static' }, '• ' + opt);
                            })
                          )
                        : null
                  ));
                });
              }
              var hasQInvestigation = (Array.isArray(msg.activity) && msg.activity.length > 0)
                || (Array.isArray(msg.probes) && msg.probes.length > 0);
              if (hasQInvestigation) {
                questionElements.push(h(AskActivityLog, { key: 'activity', msg: msg }));
              }
              return h('div', { key: i, className: 'coe-ask-msg coe-ask-msg--assistant' }, questionElements);
            }
            if (msg.streaming) {
              return h('div', { key: i, className: 'coe-ask-msg coe-ask-msg--' + msg.role },
                h(AskActivityLog, { msg: msg })
              );
            }
            var hasInvestigation = msg.role === 'assistant'
              && ((Array.isArray(msg.activity) && msg.activity.length > 0)
                  || (Array.isArray(msg.probes) && msg.probes.length > 0));
            var msgImages = Array.isArray(msg.images) ? msg.images : [];
            return h('div', { key: i, className: 'coe-ask-msg coe-ask-msg--' + msg.role },
              msgImages.length > 0
                ? h('div', { className: 'coe-ask-msg-images' },
                    msgImages.map(function(img, ii) {
                      return h('img', {
                        key: img.id || ii,
                        className: 'coe-ask-msg-image',
                        src: img.dataUrl,
                        alt: img.name || 'attached image'
                      });
                    })
                  )
                : null,
              msg.text
                ? h('div', { className: 'coe-ask-msg-text' },
                    msg.role === 'assistant' ? renderMarkdown(msg.text) : msg.text
                  )
                : null,
              msg.applied ? h('div', { className: 'coe-ask-applied' },
                '✓ Changes applied',
                msg.hyperparamsApplied ? h('div', { className: 'coe-ask-applied-detail' }, formatHyperparamsApplied(msg.hyperparamsApplied)) : null,
                msg.edgeChangesApplied ? h('div', { className: 'coe-ask-applied-detail' }, formatEdgeChangesApplied(msg.edgeChangesApplied)) : null
              ) : null,
              hasInvestigation ? h(AskActivityLog, { msg: msg }) : null
            );
          }),
      (loading && !messages.some(function(m) { return m && m.streaming; })) ? h('div', { className: 'coe-ask-msg coe-ask-msg--assistant' },
        h('div', { className: 'coe-ask-typing' },
          h('span', { className: 'coe-ask-typing-dot' }),
          h('span', { className: 'coe-ask-typing-dot' }),
          h('span', { className: 'coe-ask-typing-dot' })
        )
      ) : null,
      h('div', { ref: bottomRef })
    ),
    showQuickActions
      ? h('div', { className: 'coe-ask-quick-actions' },
          quickActions.map(function(qa, idx) {
            return h('button', {
              key: idx,
              className: 'coe-ask-quick-chip',
              onClick: function() { doAsk(qa.prompt); },
              disabled: loading
            }, qa.label);
          })
        )
      : null,
    pendingImages.length > 0
      ? h('div', { className: 'coe-ask-pending-images' },
          pendingImages.map(function(img) {
            return h('div', { key: img.id, className: 'coe-ask-pending-image' },
              h('img', { src: img.dataUrl, alt: img.name }),
              h('button', {
                type: 'button',
                className: 'coe-ask-pending-remove',
                onClick: function() { removePendingImage(img.id); },
                title: 'Remove image'
              }, '×')
            );
          })
        )
      : null,
    autoFixSource
      ? h('div', { className: 'coe-ask-input-row coe-ask-input-row--locked' },
          h('span', { className: 'coe-ask-locked-label' },
            autoFixSource === 'doctor' ? 'Applying fix from Workflow Doctor…' : 'Autopilot is fixing this node…'
          )
        )
      : h('div', {
          className: 'coe-ask-input-row' + (isDragging ? ' coe-ask-input-row--dragging' : ''),
          onDragOver: handleDragOverOnInput,
          onDragLeave: handleDragLeaveOnInput,
          onDrop: handleDropOnInput
        },
          h('input', {
            ref: fileInputRef,
            type: 'file',
            accept: 'image/*',
            multiple: true,
            style: { display: 'none' },
            onChange: handleFileInputChange
          }),
          h('button', {
            type: 'button',
            className: 'coe-ask-attach-btn',
            onClick: handleAttachClick,
            disabled: loading,
            title: 'Attach image'
          }, h(AttachIcon)),
          h('div', { className: 'coe-ask-input-wrap' },
            h('textarea', {
              className: 'coe-ask-input',
              placeholder: loading ? 'Type a new message to replace the current one...' : (pendingImages.length > 0 ? 'Add a note about the image (optional)...' : 'Ask a question, paste/drop an image, or describe a change...'),
              value: question,
              rows: 1,
              onChange: function(e) { setQuestion(e.target.value); },
              onKeyDown: handleKeyDown,
              onPaste: handlePasteOnInput,
              spellCheck: false
            })
          ),
          (function() {
            var trimmed = question.trim();
            var hasImages = pendingImages.length > 0;
            var isStop = loading && !trimmed && !hasImages;
            var canSend = !!trimmed || hasImages;
            return h('button', {
              className: 'coe-ask-btn' + (isStop ? ' coe-ask-btn-stop' : ''),
              onClick: isStop ? cancelAsk : handleAsk,
              disabled: !isStop && !canSend,
              title: isStop ? 'Stop' : 'Send'
            }, h(isStop ? StopIcon : SendIcon));
          })()
        )
  );
}

// ── Default code ─────────────────────────────────────────────────────────────

function DEFAULT_CODE() {
  return 'async function execute(inputs, settings, vars) {\n  // inputs — data from connected nodes, e.g. inputs[0]\n  // settings — this node\'s settings, e.g. settings["API Key"]\n  // vars — workflow variables, e.g. vars["Website Address"]\n  return inputs[0] ?? null;\n}\n';
}

function buildFriendlyChangeMessage(promptText, oldCode, newCode) {
  var oldText = String(oldCode || '').trim();
  var nextText = String(newCode || '').trim();
  if (!nextText || nextText === oldText) {
    return 'I checked your request, but the code stayed the same.';
  }

  var oldLines = oldText ? oldText.split('\n').length : 0;
  var nextLines = nextText ? nextText.split('\n').length : 0;
  var lineDelta = nextLines - oldLines;
  var sizeHint = '';
  if (Math.abs(lineDelta) >= 3) {
    sizeHint = lineDelta > 0 ? ' and is now a bit more detailed' : ' and is now a bit shorter';
  }

  var promptLabel = String(promptText || '').trim();
  var prefix = promptLabel ? ('Applied: "' + promptLabel + '". ') : 'Applied your request. ';
  return prefix + 'The node behavior has been updated' + sizeHint + '.';
}

function safePromptJson(value, maxChars) {
  try {
    var text = JSON.stringify(value, null, 2);
    var limit = Number(maxChars) || 0;
    if (limit > 0 && text.length > limit) {
      return text.slice(0, Math.max(0, limit - 20)) + '\n...[truncated]';
    }
    return text;
  } catch (error) {
    return String(value);
  }
}

// ── Main editor ───────────────────────────────────────────────────────────────

function CustomObjectEditor(props) {
  var onSave = props.onSave;
  var onClose = props.onClose;
  var initialData = props.initialData || {};
  var mode = props.mode === 'node' ? 'node' : 'template';

  var [name, setName] = useState(initialData.title || '');
  var [logoUrl, setLogoUrl] = useState(initialData.logoUrl || '');
  var [borderColor, setBorderColor] = useState(initialData.borderColor || '');
  var [code, setCode] = useState(initialData.code || DEFAULT_CODE());
  var [logoError, setLogoError] = useState(false);
  var [hyperparameters, setHyperparameters] = useState(initialData.hyperparameters || []);
  var [generationPrompt, setGenerationPrompt] = useState(initialData.generationPrompt || '');
  var [changeHistory, setChangeHistory] = useState(Array.isArray(initialData.changeHistory) ? initialData.changeHistory : []);
  var [activeTab, setActiveTab] = useState(mode === 'node' ? 'ask' : 'code');
  var [lastApplyFeedback, setLastApplyFeedback] = useState('');
  var [askMessages, setAskMessages] = useState([]);
  var [pendingAsk, setPendingAsk] = useState(props.initialPendingAsk || null);
  var [pendingSettingsSave, setPendingSettingsSave] = useState(false);
  var [askInProgress, setAskInProgress] = useState(false);
  var [showCloseConfirm, setShowCloseConfirm] = useState(false);
  var pendingNodeUpdateRef = useRef(null);
  var logoColorRequestRef = useRef(0);
  // Populated by AskPanel — calling it cancels the in-flight stream.
  var askCancelRef = useRef(null);

  function handleClose() {
    if (askInProgress) {
      setShowCloseConfirm(true);
      return;
    }
    if (typeof onClose === 'function') onClose();
  }

  function confirmCloseDuringAsk() {
    setShowCloseConfirm(false);
    if (typeof askCancelRef.current === 'function') {
      try { askCancelRef.current(); } catch (e) { /* noop */ }
    }
    if (typeof onClose === 'function') onClose();
  }

  function applyDetectedBorderColor(nextBorderColor) {
    if (!nextBorderColor) return;
    setBorderColor(nextBorderColor);
    if (mode === 'node' && typeof props.onSilentSave === 'function' && initialData.id) {
      props.onSilentSave({
        id: initialData.id,
        logoUrl: logoUrl.trim(),
        borderColor: nextBorderColor
      });
    }
  }

  function handleGenerateResult(data, promptText) {
    var nextCode = data && data.code ? String(data.code) : code;
    var promptValue = String(promptText || '').trim();
    if (promptValue) setGenerationPrompt(promptValue);
    if (data.name) setName(data.name);
    if (Array.isArray(data.hyperparameters) && data.hyperparameters.length) {
      setHyperparameters(data.hyperparameters);
    }
    if (data.code) setCode(nextCode);
    if (mode === 'node') {
      setLastApplyFeedback(buildFriendlyChangeMessage(promptText, code, nextCode));
      setActiveTab('code');
    }
  }

  function buildNodePrompt(promptText) {
    var nodeTitle = String(initialData.title || name || 'Custom node').trim();
    var paramsJson = JSON.stringify(Array.isArray(hyperparameters) ? hyperparameters : []);
    var availableVariables = initialData && initialData.availableVariables
      ? initialData.availableVariables
      : {};
    var hasLastInput = initialData && Object.prototype.hasOwnProperty.call(initialData, 'lastInput');
    var lastInput = hasLastInput ? initialData.lastInput : null;
    return [
      'Update this existing custom workflow node code based on the request below.',
      'Keep the exact function signature: async function execute(inputs, settings, vars).',
      'NEVER rename inputs, settings, or vars to anything else.',
      'Keep current behavior unless the request asks to change it.',
      '',
      'Coding template (MUST follow):',
      'async function execute(inputs, settings, vars) {',
      '  // inputs — data from connected nodes, e.g. inputs[0]',
      '  // settings — this node\'s settings, e.g. settings["API Key"]',
      '  // vars — workflow variables, e.g. vars["Website Address"]',
      '  return inputs[0] ?? null;',
      '}',
      '',
      'Node title: ' + nodeTitle,
      'Current hyperparameters: ' + paramsJson,
      'Input contract: inputs is an array — use inputs[0] for the primary upstream value.',
      '',
      'Available vars with current values:',
      safePromptJson(availableVariables, 2400),
      '',
      'Last input from previous node:',
      hasLastInput ? safePromptJson(lastInput, 1800) : 'No input received yet.',
      '',
      'Current code:',
      '```javascript',
      String(code || ''),
      '```',
      '',
      'Requested change:',
      promptText
    ].join('\n');
  }

  function handleNodePromptGenerate(promptText) {
    var debugPayload = {
      nodeTitle: String(initialData.title || name || 'Custom node').trim(),
      promptText: String(promptText || '').trim(),
      availableVariables: initialData && initialData.availableVariables ? initialData.availableVariables : {},
      lastInput: initialData && Object.prototype.hasOwnProperty.call(initialData, 'lastInput')
        ? initialData.lastInput
        : null
    };
    console.log('[node-prompt-apply] payload:', debugPayload);

    return request('/custom-objects/generate', {
      method: 'POST',
      body: {
        prompt: buildNodePrompt(promptText),
        context: { startParams: initialData.startParams || null }
      }
    }).then(function(data) {
      return {
        name: name,
        hyperparameters: data && Array.isArray(data.hyperparameters) ? data.hyperparameters : hyperparameters,
        code: data && data.code ? data.code : code
      };
    });
  }

  function handleAutoSave(newCode) {
    handleAutoSaveWithHistory(newCode, changeHistory);
  }

  function handleAutoSaveWithHistory(newCode, updatedHistory, hyperparamsOverride, paramValuesOverride) {
    var nodeId = initialData.id;
    if (!nodeId) return;
    var effectiveHyperparams = Array.isArray(hyperparamsOverride) ? hyperparamsOverride : hyperparameters;
    if (mode === 'node') {
      if (typeof props.onSilentSave === 'function') {
        var silentPayload = { id: nodeId, code: newCode, changeHistory: updatedHistory };
        if (Array.isArray(hyperparamsOverride)) silentPayload.hyperparameters = hyperparamsOverride;
        if (paramValuesOverride && typeof paramValuesOverride === 'object') silentPayload.paramValues = paramValuesOverride;
        props.onSilentSave(silentPayload);
      }
      return;
    }
    request('/custom-objects/' + nodeId, {
      method: 'PUT',
      body: {
        id: nodeId,
        kind: 'workflow-node',
        nodeType: 'card',
        category: 'custom',
        title: (name.trim() || initialData.title || ''),
        subtitle: 'Custom node',
        logoUrl: logoUrl.trim(),
        borderColor: borderColor,
        code: newCode,
        generationPrompt: String(generationPrompt || '').trim(),
        changeHistory: updatedHistory,
        hyperparameters: effectiveHyperparams,
        icon: 'custom'
      }
    }).catch(function(err) {
      console.error('[AutoSave] Failed to save code:', err);
    });
  }

  function handleSave() {
    if (!name.trim()) return;
    onSave({
      id: initialData.id || ('custom-' + Date.now()),
      kind: 'workflow-node',
      nodeType: 'card',
      category: 'custom',
      title: name.trim(),
      subtitle: 'Custom node',
      logoUrl: logoUrl.trim(),
      borderColor: borderColor,
      code: code,
      generationPrompt: String(generationPrompt || '').trim(),
      changeHistory: changeHistory,
      hyperparameters: hyperparameters,
      icon: 'custom'
    });
  }

  function handleOverlayClick(e) {
    if (e.target === e.currentTarget) handleClose();
  }

  function handleLogoChange(e) {
    setLogoUrl(e.target.value);
    setLogoError(false);
  }

  function handleLogoPreviewLoad(currentLogoUrl) {
    var requestId = logoColorRequestRef.current + 1;
    logoColorRequestRef.current = requestId;

    loadImageDominantBorderColor(currentLogoUrl).then(function(nextBorderColor) {
      if (logoColorRequestRef.current !== requestId) return;
      applyDetectedBorderColor(nextBorderColor);
    }).catch(function() {
      if (logoColorRequestRef.current !== requestId) return;
    });
  }

  function hasSignificantHyperparamChange(prev, next) {
    if (!prev) prev = [];
    if (!next) next = [];
    if (prev.length !== next.length) return true;
    for (var i = 0; i < prev.length; i++) {
      if (prev[i].type !== next[i].type) return true;
    }
    return false;
  }

  function handlePendingAskComplete(result) {
    // `result` is { code, hasEdgeChanges } from AskPanel. Older callers may
    // pass null — normalize to the same shape.
    var safe = (result && typeof result === 'object') ? result : { code: null, hasEdgeChanges: false };
    var newCode = safe.code || null;
    if (pendingSettingsSave && pendingNodeUpdateRef.current) {
      var update = Object.assign({}, pendingNodeUpdateRef.current, newCode ? { code: newCode } : {});
      onSave(update);
      setPendingSettingsSave(false);
      pendingNodeUpdateRef.current = null;
    }
    if (typeof props.onAutoFixComplete === 'function') {
      props.onAutoFixComplete(safe);
    }
  }

  function buildNodeUpdate(overrides) {
    return Object.assign({
      id: initialData.id,
      code: code,
      logoUrl: logoUrl.trim(),
      borderColor: borderColor,
      hyperparameters: hyperparameters
    }, overrides || {});
  }

  var isEditing = Boolean(initialData.id) || mode === 'node';
  var showTemplateFields = mode !== 'node';
  var titleText = mode === 'node'
    ? 'Edit this step'
    : (isEditing ? 'Edit Custom Object' : 'New Custom Object');
  var subtitleText = mode === 'node'
    ? 'Tweak how this step works — just for this workflow'
    : 'Define a reusable workflow node with custom logic';
  var saveLabel = mode === 'node'
    ? 'Update Node'
    : (isEditing ? 'Update Object' : 'Save Object');

  return h('div', { className: 'coe-overlay', onClick: handleOverlayClick },
    h('div', { className: 'coe-modal' },

      h('div', { className: 'coe-header' },
        h('div', null,
          h('div', { className: 'coe-title' },
            titleText,
            mode === 'node' && (name || initialData.title)
              ? h('span', { className: 'coe-title-node-name' }, name || initialData.title)
              : null
          ),
          h('div', { className: 'coe-subtitle' }, subtitleText)
        ),
        h('button', { className: 'coe-close-btn', onClick: handleClose }, h(CloseIcon))
      ),

      h('div', { className: 'coe-body' },

        showTemplateFields
          ? h(GenerateBar, { onResult: handleGenerateResult })
          : null,

        showTemplateFields
          ? h(SettingsFields, {
              showName: true,
              name: name,
              onNameChange: setName,
              logoUrl: logoUrl,
              onLogoChange: handleLogoChange,
              onLogoPreviewLoad: handleLogoPreviewLoad,
              logoError: logoError,
              setLogoError: setLogoError,
              borderColor: borderColor,
              onBorderColorChange: setBorderColor
            })
          : null,

        null,

        mode === 'node'
          ? h('div', { className: 'coe-tabs' },
              h('button', {
                className: 'coe-tab-btn' + (activeTab === 'ask' ? ' coe-tab-btn-active' : ''),
                onClick: function() { setActiveTab('ask'); }
              }, 'Ask'),
              h('button', {
                className: 'coe-tab-btn' + (activeTab === 'settings' ? ' coe-tab-btn-active' : ''),
                onClick: function() { setActiveTab('settings'); }
              }, 'Settings'),
              h('button', {
                className: 'coe-tab-code-link' + (activeTab === 'code' ? ' coe-tab-code-link-active' : ''),
                onClick: function() { setActiveTab('code'); }
              }, 'Code')
            )
          : null,

        mode === 'node' && activeTab === 'ask'
          ? h(AskPanel, {
              getCode: function() { return code; },
              onCodeChange: function(newCode, changeDesc, newHyperparams, newParamValues) {
                setCode(newCode);
                if (Array.isArray(newHyperparams)) setHyperparameters(newHyperparams);
                var historyToUse = changeHistory;
                if (changeDesc) {
                  historyToUse = changeHistory.concat(changeDesc);
                  setChangeHistory(historyToUse);
                }
                handleAutoSaveWithHistory(newCode, historyToUse, newHyperparams || null, newParamValues || null);
              },
              // AskCode set / cleared the propPath on one or more outgoing
              // connections from this node. Forward straight to the canvas via
              // onSilentSave — connections live on the workflow, not on the
              // node, so they go through a dedicated payload field.
              onConnectionUpdates: function(connectionUpdates, changeDesc) {
                var nodeId = initialData.id;
                if (!nodeId || !Array.isArray(connectionUpdates) || !connectionUpdates.length) return;
                var historyToUse = changeHistory;
                if (changeDesc) {
                  historyToUse = changeHistory.concat(changeDesc);
                  setChangeHistory(historyToUse);
                }
                if (typeof props.onSilentSave === 'function') {
                  props.onSilentSave({
                    id: nodeId,
                    connectionUpdates: connectionUpdates,
                    changeHistory: historyToUse
                  });
                }
              },
              changeHistory: changeHistory,
              generationPrompt: generationPrompt,
              initialData: initialData,
              messages: askMessages,
              setMessages: setAskMessages,
              pendingAsk: pendingAsk,
              autoFixReason: props.autoFixReason || '',
              autoFixSource: props.autoFixSource || null,
              onPendingAskConsumed: function() { setPendingAsk(null); },
              onPendingAskComplete: handlePendingAskComplete,
              onAskingChange: setAskInProgress,
              cancelRef: askCancelRef
            })
          : null,

        mode === 'node' && activeTab === 'settings'
          ? h(SettingsFields, {
              logoUrl: logoUrl,
              onLogoChange: handleLogoChange,
              onLogoPreviewLoad: handleLogoPreviewLoad,
              logoError: logoError,
              setLogoError: setLogoError,
              borderColor: borderColor,
              onBorderColorChange: setBorderColor
            })
          : null,

        (mode !== 'node' || activeTab === 'code')
          ? h('div', { className: 'coe-editor-section' },
              mode !== 'node'
                ? h('div', { className: 'coe-editor-header' },
                    h('span', { className: 'coe-editor-label' }, 'JavaScript Code'),
                    h('span', { className: 'coe-editor-hint' }, 'VS Code - Light')
                  )
                : null,
              h('div', { className: 'coe-editor-wrap' },
                h(MonacoCodeEditor, {
                  value: code,
                  onChange: setCode,
                  readOnly: false
                })
              )
            )
          : null
      ),

      h('div', { className: 'coe-footer' },
        mode !== 'node' ? h('button', { className: 'coe-btn-cancel', onClick: handleClose }, 'Cancel') : null,
        mode === 'node' && activeTab === 'code'
          ? h('button', {
              className: 'coe-btn-save',
              onClick: function() { onSave(buildNodeUpdate()); }
            }, 'Update Code')
          : null,
        mode === 'node' && activeTab === 'settings'
          ? h('button', {
              className: 'coe-btn-save',
              disabled: pendingSettingsSave,
              onClick: function() {
                var prevParams = initialData.hyperparameters || [];
                if (hasSignificantHyperparamChange(prevParams, hyperparameters) && hyperparameters.length > 0) {
                  var hyperStr = hyperparameters.map(function(p) {
                    return p.key + ' (' + p.type + ')' + (p.default ? ': ' + p.default : '');
                  }).join(', ');
                  pendingNodeUpdateRef.current = buildNodeUpdate();
                  setPendingSettingsSave(true);
                  setPendingAsk('adjust your case base from this hyperparams: ' + hyperStr);
                  setActiveTab('ask');
                } else {
                  onSave(buildNodeUpdate());
                }
              }
            }, pendingSettingsSave ? 'Updating...' : 'Update Settings')
          : null,
        mode !== 'node'
          ? h('button', {
              className: 'coe-btn-save',
              onClick: handleSave,
              disabled: !name.trim()
            }, saveLabel)
          : null
      ),
      showCloseConfirm
        ? h(ConfirmDialog, {
            title: 'Cancel in-progress Ask?',
            message: 'A request is still streaming. Closing will cancel it.',
            confirmLabel: 'Close & Cancel',
            cancelLabel: 'Keep Open',
            danger: true,
            onConfirm: confirmCloseDuringAsk,
            onCancel: function() { setShowCloseConfirm(false); }
          })
        : null
    )
  );
}

module.exports = CustomObjectEditor;
